import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type FormEvent,
} from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import {
  BadgeDollarSign,
  Bot,
  BotMessageSquare,
  ClipboardCheck,
  ClipboardList,
  Flag,
  LayoutDashboard,
  LogOut,
  Menu,
  PackageSearch,
  Printer,
  Search,
  Settings,
  Store,
  Users,
  Sparkles,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { signOut } from '@/lib/sign-out';
import { AGENT_WORK_ORDER_PERMISSIONS } from '@/lib/agent-work-orders';
import { Avatar } from '@/components/ui';
import logoHorizontal from '@/assets/vapt-menu-logo-horizontal-color.svg';
import styles from './Shell.module.css';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Store;
  adminOnly?: boolean;
  /** Aparece só para quem tem ao menos uma destas permissões (a API revalida). */
  anyPermission?: readonly string[];
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/tenants', label: 'Lojas', icon: Store },
  { to: '/orders/search', label: 'Pedidos', icon: PackageSearch },
  { to: '/operators', label: 'Operadores', icon: Users, adminOnly: true },
  { to: '/feature-flags', label: 'Feature flags', icon: Flag, adminOnly: true },
  { to: '/agentes', label: 'Agentes de IA', icon: Bot, adminOnly: true },
  { to: '/propostas-agentes', label: 'Propostas de agentes', icon: ClipboardCheck },
  // Ordens de serviço em lote para o agente de escrita (change platform-mcp-writes, 1.17).
  { to: '/lojas-para-agir', label: 'Lojas para agir', icon: ClipboardList, anyPermission: AGENT_WORK_ORDER_PERMISSIONS },
  { to: '/precos', label: 'Planos e preços', icon: BadgeDollarSign },
  { to: '/vapt-ia', label: 'Vapt IA', icon: Sparkles },
  { to: '/atendente-ia', label: 'Atendente IA: custos', icon: BotMessageSquare, adminOnly: true },
  { to: '/impressao', label: 'Impressão', icon: Printer },
];

const PAGE_TITLES: Array<{ prefix: string; title: string; subtitle: string }> = [
  { prefix: '/dashboard', title: 'Visão geral', subtitle: 'Acompanhe a saúde da plataforma' },
  { prefix: '/tenants', title: 'Lojas', subtitle: 'Lojas cadastradas na plataforma' },
  { prefix: '/orders', title: 'Pedidos', subtitle: 'Localize pedidos de qualquer restaurante' },
  { prefix: '/operators', title: 'Operadores', subtitle: 'Equipe com acesso ao backoffice' },
  { prefix: '/feature-flags', title: 'Feature flags', subtitle: 'Lançamentos controlados por flag' },
  { prefix: '/agentes', title: 'Agentes de IA', subtitle: 'Perfis do Hermes, concessões e uso do MCP' },
  { prefix: '/propostas-agentes', title: 'Propostas de agentes', subtitle: 'O que o agente pediu e só uma pessoa aprova' },
  { prefix: '/precos', title: 'Planos e preços', subtitle: 'Preço e cota de cada plano, pacotes de atendimentos e histórico' },
  { prefix: '/vapt-ia', title: 'Vapt IA', subtitle: 'Consumo do agente contra o teto mensal' },
  { prefix: '/atendente-ia', title: 'Atendente IA: custos', subtitle: 'Tarifas da Meta e tetos em dólar do atendente' },
  { prefix: '/impressao', title: 'Impressão', subtitle: 'Computadores e cupons dos pontos de produção' },
  { prefix: '/profile', title: 'Perfil', subtitle: 'Sua conta e segurança' },
  { prefix: '/lojas-para-agir', title: 'Lojas para agir', subtitle: 'Ordens de serviço em lote para o agente de escrita' },
];

const MOBILE_QUERY = '(max-width: 768px)';

/**
 * Observa o breakpoint. `useSyncExternalStore` é o encaixe certo: `matchMedia`
 * é uma fonte externa com assinatura própria, e ler por ele evita o
 * setState-dentro-de-efeito que dispara render em cascata. `matchMedia` pode
 * não existir (jsdom nos testes), e a tela pode girar com o app aberto — daí
 * o listener.
 */
function subscribeToViewport(onChange: () => void): () => void {
  const mql = window.matchMedia?.(MOBILE_QUERY);
  if (!mql) return () => {};
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

function useIsMobile(): boolean {
  return useSyncExternalStore(
    subscribeToViewport,
    () => window.matchMedia?.(MOBILE_QUERY).matches === true,
    () => false,
  );
}

export function Shell() {
  const operator = useAuthStore((s) => s.operator);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  // O drawer guarda a rota em que foi aberto: navegar torna o estado obsoleto,
  // e ele fecha por derivação em vez de por efeito. Mesma ideia para o
  // desktop, onde o drawer simplesmente não existe.
  const [drawerAt, setDrawerAt] = useState<string | null>(null);
  const [searchRequested, setSearchRequested] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const firstNavItemRef = useRef<HTMLAnchorElement>(null);

  const drawerOpen = isMobile && drawerAt === location.pathname;
  const searchOpen = isMobile && searchRequested;

  const pageInfo =
    PAGE_TITLES.find((entry) => location.pathname.startsWith(entry.prefix)) ??
    PAGE_TITLES[0];

  const closeDrawer = useCallback(() => {
    setDrawerAt(null);
    // Devolve o foco a quem abriu: sem isso o teclado recomeça do topo.
    menuButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', onKeyDown);
    firstNavItemRef.current?.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen, closeDrawer]);

  function logout() {
    // "Sair" sempre sai (revisão E16 da C8): a marca do "Sair" explícito, a memória limpa e a
    // autorização de agente pendente nesta aba saem agora, e o `/logout` segue em segundo plano.
    // Se a API não confirmar, a tela de login mostra o erro e o "Tentar de novo"; a marca impede
    // que o próximo carregamento restaure a sessão pelo cookie.
    void signOut();
    navigate('/login', { replace: true });
  }

  function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const value = (new FormData(form).get('q') as string | null)?.trim() ?? '';
    navigate(value ? `/tenants?q=${encodeURIComponent(value)}` : '/tenants');
    form.reset();
    setSearchRequested(false);
  }

  const visibleNavItems = NAV_ITEMS.filter(
    (item) =>
      (!item.adminOnly || operator?.role === 'Admin') &&
      (!item.anyPermission || item.anyPermission.some((p) => operator?.permissions?.includes(p))),
  );

  return (
    <div className={styles.shell}>
      {drawerOpen && (
        <button
          type="button"
          className={styles.overlay}
          aria-label="Fechar navegação"
          onClick={closeDrawer}
        />
      )}

      <aside
        id="backoffice-nav"
        className={`${styles.sidebar} ${drawerOpen ? styles.sidebarOpen : ''}`}
        // Com o drawer fechado no mobile a sidebar está fora da tela: escondê-la
        // da árvore de acessibilidade evita foco em link invisível.
        aria-hidden={isMobile && !drawerOpen}
      >
        <div className={styles.brand}>
          <img src={logoHorizontal} alt="Vapt Menu" className={styles.brandLogo} />
        </div>
        <div className={styles.sectionLabel}>Backoffice</div>
        <nav className={styles.nav}>
          {visibleNavItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                ref={index === 0 ? firstNavItemRef : undefined}
                tabIndex={isMobile && !drawerOpen ? -1 : undefined}
                className={({ isActive }) =>
                  `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                }
              >
                <Icon size={18} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className={styles.operator}>
          <Avatar name={operator?.email ?? '?'} size="sm" />
          <div className={styles.operatorInfo}>
            <div className={styles.operatorEmail} title={operator?.email}>
              {operator?.email}
            </div>
            <div className={styles.operatorRole}>{operator?.role} · plataforma</div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/profile')}
            title="Perfil"
            aria-label="Perfil"
            className={styles.iconButton}
          >
            <Settings size={18} />
          </button>
          <button
            type="button"
            onClick={logout}
            title="Sair"
            aria-label="Sair"
            className={styles.iconButton}
          >
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      <div className={styles.content}>
        <div className={styles.topbar}>
          <button
            type="button"
            ref={menuButtonRef}
            className={styles.menuButton}
            aria-label="Abrir navegação"
            aria-expanded={drawerOpen}
            aria-controls="backoffice-nav"
            onClick={() => (drawerOpen ? closeDrawer() : setDrawerAt(location.pathname))}
          >
            <Menu size={22} />
          </button>
          <img src={logoHorizontal} alt="Vapt Menu" className={styles.topbarLogo} />
          <span className={styles.topbarSpacer} />
          <button
            type="button"
            className={styles.searchToggle}
            aria-label="Buscar loja"
            aria-expanded={searchOpen}
            onClick={() => setSearchRequested((open) => !open)}
          >
            <Search size={20} />
          </button>
        </div>

        <header className={styles.header}>
          <div className={styles.headerRow}>
            <div className={styles.pageTitleBlock}>
              <h1 className={styles.pageTitle}>{pageInfo.title}</h1>
              <div className={styles.pageSubtitle}>{pageInfo.subtitle}</div>
            </div>
            <form
              onSubmit={submitSearch}
              className={`${styles.searchForm} ${searchOpen ? styles.searchFormOpen : ''}`}
            >
              <span className={styles.searchIcon}>
                <Search size={17} />
              </span>
              <input
                name="q"
                placeholder="Buscar loja por nome ou slug…"
                aria-label="Buscar loja"
                className={styles.searchInput}
              />
            </form>
          </div>
        </header>

        <main className={styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
